import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ProjectCard from '../ProjectCard';
import ProjectModal from '../ProjectModal';
import ProjectFilter from '../ProjectFilter';

// Import des images
import artifullImg from '../../assets/artifull.jpg';
import linguaImg from '../../assets/lingua.jpg';
import trackersportImg from '../../assets/trackersport.png';
import ecommerceImg from '../../assets/ecommerce-react.jpg';
import portfolioImg from '../../assets/portfolio.jpg';
import gvwTechImg from '../../assets/gvw-tech.jpg';
import jardiniageImg from '../../assets/gvw-tech-jardinage.jpg';
import kinesitherapeuteImg from '../../assets/kinesitherapeute.jpg';
import pizzeriaImg from '../../assets/pizzeria.jpg';
import devGvwImg from '../../assets/dev-gvw-tech.webp';

/* ── Étoiles locales (dark mode) ── */
const StarsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  display: block;
`;

const StarDot = styled.div`
  position: absolute;
  border-radius: 50%;
  background: white;
`;

function SectionStars({ count = 120 }) {
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const size = Math.random() * 2 + 0.5;
      arr.push({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: size,
        height: size,
        opacity: Math.random() * 0.45 + 0.08,
        animDelay: Math.random() * 6,
        animDuration: Math.random() * 4 + 3,
      });
    }
    return arr;
  }, [count]);

  return (
    <StarsWrapper>
      {stars.map(s => (
        <StarDot
          key={s.id}
          style={{
            left: s.left,
            top: s.top,
            width: s.width,
            height: s.height,
            opacity: s.opacity,
            animation: `twinkle ${s.animDuration}s ease-in-out ${s.animDelay}s infinite alternate`,
          }}
        />
      ))}
    </StarsWrapper>
  );
}

const RealisationsContainer = styled.section`
  padding: 8rem 0;
  background: linear-gradient(170deg, #1a3a5c 0%, #1e4976 40%, #2471a3 100%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 70px;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    scroll-margin-top: 60px;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0;
  }
  
  .dark-mode & {
    background: #0e1420;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(var(--secondary-rgb), 0.1) 0%, rgba(var(--secondary-rgb), 0) 70%);
    border-radius: 50%;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(var(--accent-rgb), 0.1) 0%, rgba(var(--accent-rgb), 0) 70%);
    border-radius: 50%;
    z-index: 1;
  }
`;

const SectionContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--secondary);
  position: relative;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.7rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, var(--black) 0%, var(--black) 33%, var(--secondary) 33%, var(--secondary) 66%, var(--accent) 66%, var(--accent) 100%);
    border-radius: var(--border-radius-sm);
  }
`;

const SectionDescription = styled(motion.p)`
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.1rem;
  line-height: 1.8;
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const NoProjectsMessage = styled(motion.div)`
  text-align: center;
  padding: 3rem;
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  margin: 2rem auto;
  max-width: 600px;
  
  .dark-mode & {
    background-color: var(--dark);
  }
  
  h3 {
    color: var(--primary);
    margin-bottom: 1rem;
    
    .dark-mode & {
      color: var(--white);
    }
  }
  
  p {
    color: var(--gray-600);
    
    .dark-mode & {
      color: var(--light);
    }
  }
`;

const BelgianAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .black, .yellow, .red {
    flex: 1;
  }
  
  .black {
    background-color: var(--black);
  }
  
  .yellow {
    background-color: var(--secondary);
  }
  
  .red {
    background-color: var(--accent);
  }
`;

const RealisationsSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);
  
  // Couleurs pour les catégories (inspirées du drapeau belge)
  const categoryColors = {
    // site vitrine: orange, ecommerce: red, application: blue
    siteVitrine: '#FF7A00',
    ecommerce: '#C8102E',
    application: '#007BFF'
  };
  
  const projects = [
    {
      id: 1,
      title: "Artifull",
      category: "e-commerce",
      image: artifullImg,
      description: "Application web complète de gestion d'œuvres d'art. Développée avec Java Spring Boot pour le backend et Angular pour le frontend, cette plateforme offre une expérience utilisateur moderne et performante.",
      features: [
        "Backend robuste avec Spring Boot",
        "Frontend dynamique avec Angular",
        "Spring Security pour l'authentification",
        "Spring Data JPA pour la persistance",
        "API REST complète"
      ],
      technologies: ["Java", "Spring Boot", "Angular", "TypeScript", "MySQL"],
      link: "https://artifull.gvw-dev.be"
    },
    {
      id: 2,
      title: "Lingua Duel",
      category: "application",
      image: linguaImg,
      description: "Jeu éducatif interactif pour l'apprentissage de l'anglais. Une approche ludique et engageante pour améliorer ses compétences linguistiques à travers des défis et des duels.",
      features: [
        "Interface de jeu interactive",
        "Système de défis et duels",
        "Progression et scores",
        "Apprentissage gamifié",
        "Design moderne et attractif"
      ],
      technologies: ["JavaScript", "React", "CSS3"],
      link: "https://lingua.gvw-dev.be"
    },
    {
      id: 3,
      title: "Portfolio GVW",
      category: "site-vitrine",
      image: portfolioImg,
      description: "Portfolio personnel présentant mes compétences et réalisations en développement web. Design moderne avec animations et transitions fluides.",
      features: [
        "Design moderne et élégant",
        "Animations et transitions",
        "Présentation des compétences",
        "Galerie de projets",
        "Formulaire de contact"
      ],
      technologies: ["HTML5", "CSS3", "JavaScript", "Bootstrap"],
      link: "https://portfolio.gvw-tech.be"
    },
    {
      id: 4,
      title: "GVW Tech",
      category: "application",
      image: gvwTechImg,
      description: "Site vitrine professionnel pour GVW Tech, présentant les services de développement web et les solutions digitales proposées.",
      features: [
        "Présentation des services",
        "Design professionnel",
        "Optimisation SEO",
        "Formulaire de contact",
        "Interface responsive"
      ],
      technologies: ["React", "Styled Components", "JavaScript"],
      link: "https://ifosup.gvw-tech.be/accueil"
    },
    {
      id: 5,
      title: "Pizzeria Meraviglioso",
      category: "application",
      image: pizzeriaImg,
      description: "Site vitrine pour une pizzeria avec présentation du menu, des spécialités et système de commande en ligne.",
      features: [
        "Menu interactif",
        "Galerie photos des plats",
        "Système de commande",
        "Horaires et localisation",
        "Design appétissant"
      ],
      technologies: ["HTML5", "CSS3", "JavaScript", "PHP"],
      link: "https://meraviglioso.gvw-dev.be"
    },
    {
      id: 6,
      title: "GVW Tech Jardinage",
      category: "site-vitrine",
      image: jardiniageImg,
      description: "Site vitrine moderne pour un service de jardinage, avec une interface élégante et professionnelle mettant en valeur les prestations proposées.",
      features: [
        "Catalogue de services",
        "Galerie photos des réalisations",
        "Formulaire de devis",
        "Design nature et élégant",
        "Facilité de prise de contact"
      ],
      technologies: ["HTML5", "CSS3", "JavaScript", "Bootstrap"],
      link: "https://www.gvw-tech.be"
    },
    {
      id: 7,
      title: "TrackerSport",
      category: "application",
      image: trackersportImg,
      description: "Plateforme de suivi sportif développée avec Python Django pour le backend et Angular pour le frontend. Permet aux utilisateurs de suivre leurs performances et activités sportives.",
      features: [
        "Backend Python Django",
        "Django REST Framework pour l'API",
        "Frontend Angular moderne",
        "Suivi des performances",
        "Tableau de bord personnalisé"
      ],
      technologies: ["Python", "Django", "Angular", "TypeScript", "PostgreSQL"],
      link: null
    },
    {
      id: 8,
      title: "E-Commerce React",
      category: "e-commerce",
      image: ecommerceImg,
      description: "Boutique en ligne moderne développée avec React. Interface utilisateur fluide et responsive avec gestion complète du panier et des commandes.",
      features: [
        "Interface React moderne",
        "Gestion d'état avec Redux/Hooks",
        "Panier d'achat dynamique",
        "Design responsive",
        "Expérience utilisateur optimisée"
      ],
      technologies: ["React", "Redux", "JavaScript", "CSS3", "REST API"],
      link: null
    },
    {
      id: 9,
      title: "Kinésithérapeute",
      category: "site-vitrine",
      image: kinesitherapeuteImg,
      description: "Site vitrine professionnel pour un cabinet de kinésithérapie. Présentation des services, prise de rendez-vous et informations pratiques pour les patients.",
      features: [
        "Présentation des soins proposés",
        "Prise de rendez-vous en ligne",
        "Informations pratiques",
        "Design professionnel médical",
        "Interface responsive"
      ],
      technologies: ["HTML5", "CSS3", "JavaScript", "Bootstrap"],
      link: null
    }
    ,
    {
      id: 10,
      title: "dev.gvw-tech",
      category: "site-vitrine",
      image: devGvwImg,
      description: "Site de développement et environnement de test pour GVW-Tech. Présente les fonctionnalités en cours et les versions en développement.",
      features: [
        "Environnement de test",
        "Pages de démonstration",
        "Mises à jour fréquentes",
        "Design responsive",
        "Accès aux versions dev"
      ],
      technologies: ["React", "Styled Components", "JavaScript"],
      link: "https://dev.gvw-tech.be"
    }
  ];
  
  // Filtrer les projets lorsque la catégorie active change
  useEffect(() => {
    const filtered = activeCategory === 'all' 
      ? projects 
      : projects.filter(project => project.category === activeCategory);
    
    setFilteredProjects(filtered);
  }, [activeCategory]);
  
  const openModal = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };
  
  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = 'auto';
  };
  
  // Variantes d'animation pour les éléments
  const headerVariants = {
    hidden: { opacity: 1, y: 0 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0,
        ease: "easeOut"
      } 
    }
  };
  
  const descriptionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.6, 
        delay: 0.2,
        ease: "easeOut"
      } 
    }
  };
  
  const gridVariants = {
    hidden: { opacity: 1 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0,
        delayChildren: 0
      }
    }
  };
  
  return (
    <RealisationsContainer id="realisations">
      <SectionStars count={150} />
      <SectionContent>
        <SectionHeader>
          <SectionTitle
            className="section-title"
            variants={headerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            Mes Réalisations
          </SectionTitle>
        </SectionHeader>
        
        <ProjectFilter 
          categories={['all', 'site-vitrine', 'e-commerce', 'application']}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          categoryColors={categoryColors}
        />
        
        <AnimatePresence mode="wait">
          {filteredProjects.length > 0 ? (
            <ProjectsGrid
              key="projects-grid"
              variants={gridVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {filteredProjects.map((project, index) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={index}
                  onClick={openModal}
                  categoryColors={categoryColors}
                />
              ))}
            </ProjectsGrid>
          ) : (
            <NoProjectsMessage
              key="no-projects"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <h3>Aucun projet dans cette catégorie</h3>
              <p>Veuillez sélectionner une autre catégorie ou revenir plus tard.</p>
            </NoProjectsMessage>
          )}
        </AnimatePresence>
      </SectionContent>
      
      <ProjectModal 
        project={selectedProject}
        isOpen={modalOpen}
        onClose={closeModal}
        categoryColors={categoryColors}
      />
    </RealisationsContainer>
  );
};

export default RealisationsSection;
